/**
 * SearchableSelect Component
 *
 * A reusable search input component with dropdown suggestions, similar to the select pattern
 * in DynamicForm but specifically designed for search functionality with async data fetching.
 *
 * Features:
 * - Debounced search with customizable delay
 * - Async option fetching with loading states
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Click outside to close
 * - Customizable minimum characters before search
 * - Error handling and display
 * - Required field support
 *
 * Usage:
 * ```tsx
 * <SearchableSelect
 *   placeholder="Search for suppliers..."
 *   label="Select Supplier"
 *   onSearch={async (query) => {
 *     // Return array of SearchOption objects
 *     return [
 *       { label: "Supplier 1", value: "1", data: {...} },
 *       { label: "Supplier 2", value: "2", data: {...} }
 *     ];
 *   }}
 *   onSelect={(option) => {
 *     console.log('Selected:', option);
 *   }}
 *   minChars={2}
 *   debounceMs={300}
 * />
 * ```
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Input } from "../index";

export interface SearchOption {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
  data?: any; // Additional data for the option
}

export interface SearchableSelectProps {
  placeholder?: string;
  onSearch?: (query: string) => Promise<SearchOption[]> | SearchOption[];
  onSelect?: (option: SearchOption) => void;
  onChange?: (value: string) => void;
  value?: string;
  label?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  loading?: boolean;
  minChars?: number;
  debounceMs?: number;
  clearOnSelect?: boolean;
  showDropdownOnFocus?: boolean;
  error?: string;
  name?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  placeholder = "Search...",
  onSearch,
  onSelect,
  onChange,
  value = "",
  label,
  required,
  disabled,
  className,
  loading,
  minChars = 1,
  debounceMs = 300,
  clearOnSelect = false,
  showDropdownOnFocus = false,
  error,
  name,
}) => {
  const [searchValue, setSearchValue] = useState(value);
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const onSearchRef = useRef(onSearch);
  const justSelectedRef = useRef(false);

  // Keep the onSearch ref updated
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  // Update internal state when external value changes
  useEffect(() => {
    setSearchValue(value);
  }, [value]);

  // Debounced search effect
  useEffect(() => {
    // Skip search if we just made a selection
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchValue.length >= minChars && onSearchRef.current) {
      debounceRef.current = setTimeout(async () => {
        setIsLoading(true);
        try {
          const results = await onSearchRef.current!(searchValue);
          setOptions(results);
          setIsOpen(true);
          setHighlightedIndex(-1);
        } catch (error) {
          console.error("Search error:", error);
          setOptions([]);
        } finally {
          setIsLoading(false);
        }
      }, debounceMs);
    } else if (searchValue.length < minChars) {
      setOptions([]);
      setIsOpen(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchValue, minChars, debounceMs]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchValue(newValue);
    onChange?.(newValue);
  };

  const handleOptionSelect = (option: SearchOption) => {
    if (option.disabled) return;

    // Set flag to prevent immediate re-search
    justSelectedRef.current = true;

    // Clear the timeout to prevent any pending search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    onSelect?.(option);

    if (clearOnSelect) {
      setSearchValue("");
      onChange?.("");
    } else {
      const displayValue =
        typeof option.label === "string" ? option.label : option.value;
      setSearchValue(displayValue);
      onChange?.(displayValue);
    }

    setIsOpen(false);
    setHighlightedIndex(-1);
    setOptions([]); // Clear options to prevent any re-triggers
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && options.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : 0,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : options.length - 1,
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && options[highlightedIndex]) {
          handleOptionSelect(options[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const renderLabel = (
    label: React.ReactNode,
    required?: boolean,
  ): ReactNode => (
    <span className="flex items-center">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </span>
  );

  return (
    <div ref={containerRef} className="relative">
      <Input
        type="text"
        name={name}
        label={label ? renderLabel(label, required) : undefined}
        placeholder={placeholder}
        value={searchValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        required={required}
        disabled={disabled}
        className={className}
        endIcon={
          isLoading || loading ? (
            <div className="w-4 h-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          )
        }
      />

      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={optionsRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
              {isLoading ? "Searching..." : "No results found"}
            </div>
          ) : (
            options.map((option, index) => (
              <div
                key={`${option.value}-${index}`}
                className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                  option.disabled
                    ? "text-gray-400 cursor-not-allowed"
                    : highlightedIndex === index
                      ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                      : "text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
                onClick={() => handleOptionSelect(option)}
                onMouseEnter={() =>
                  !option.disabled && setHighlightedIndex(index)
                }
              >
                {option.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
